import type { AlgoliaEventItem } from '~/composables/types/algolia'
import type { EventDescription, Supplier } from '~/composables/types/event'

/**
 * Sanitizes and validates Algolia queryID
 * QueryID must be a 32-character hexadecimal string
 * @param queryId - The queryID to sanitize
 * @returns Sanitized queryID or null if invalid
 */
export const sanitizeAlgoliaQueryId = (
  queryId: string | null | undefined
): string | null => {
  if (!queryId) return null

  // Remove any trailing slashes or whitespace
  const cleaned = queryId.trim().replace(/\/+$/, '')

  // Validate: must be exactly 32 characters and hexadecimal
  const isValid = /^[a-f0-9]{32}$/i.test(cleaned)

  return isValid ? cleaned : null
}

/**
 * Extracts rating fields for AlgoliaEventItem from API event data.
 * Review priority: Event → Partner (Supplier) → Third Party (Google)
 * Event rating takes precedence when avg_review > 0 and total_reviews > 0.
 * Exported for unit testing.
 */
export const extractEventRatingFields = (item: {
  avg_review?: number | null
  total_reviews?: number | null
  supplier?: {
    data?: {
      avg_review?: number | null
      google_avg_review?: number | null
      google_total_reviews?: number | null
    }
  } | null
}) => {
  const eventRating = item?.avg_review ?? null
  const eventTotalReviews = item?.total_reviews ?? 0
  const supplierRating = item?.supplier?.data?.avg_review ?? null
  const supplierGoogleRating = item?.supplier?.data?.google_avg_review ?? null
  const supplierGoogleTotalRating =
    item?.supplier?.data?.google_total_reviews ?? null

  const hasValidEventReviews =
    eventRating != null &&
    eventRating > 0 &&
    eventTotalReviews != null &&
    eventTotalReviews > 0

  return {
    rating: hasValidEventReviews ? eventRating : undefined,
    avg_review: hasValidEventReviews ? eventRating : undefined,
    totalRating: hasValidEventReviews ? eventTotalReviews : undefined,
    supplier_rating: supplierRating !== null ? supplierRating : undefined,
    supplier_google_rating:
      supplierGoogleRating != null && supplierGoogleRating > 0
        ? supplierGoogleRating
        : undefined,
    supplier_google_total_rating:
      supplierGoogleTotalRating != null && supplierGoogleTotalRating > 0
        ? supplierGoogleTotalRating
        : undefined,
  }
}

export const parseEventDescriptionToAlgoliaEventItem = (
  item: EventDescription
): Partial<AlgoliaEventItem> => {
  const hashedId = eventGetters.getId(item)
  const ratingFields = extractEventRatingFields(item)

  return {
    objectID: `EventDescription::${hashedId}`,
    categories: eventGetters.getCategories(item),
    specialization: eventGetters.getSpecialization(item),
    title: eventGetters.getTitle(item),
    default_duration: eventGetters.getDefaultDuration(item),
    default_price: eventGetters.getDefaultPriceObject(item),
    original_price: eventGetters.getOriginalPriceObject(item),
    hashed_id: hashedId,
    images: eventGetters.getGallery(item),
    permalink: eventGetters.getPermalink(item),
    supplier: eventGetters.getSupplierName(item),
    next_event: eventGetters.getNextInstanceInTimestamp(item),
    ...ratingFields,
    has_mobile_classes: eventGetters.getHasMobileClasses(item),
    main_image: {
      alt: eventGetters.getFirstImageAlt(item),
      thumb: eventGetters.getFirstThumb(item),
    },
    has_promotions: eventGetters.getHasPromotion(item),
    type: eventGetters.getAttendanceMode(item),
    min_number_of_people: item.min_number_of_people ?? 0,
    max_number_of_people: item.max_number_of_people ?? 0,
    has_shipping_costs: item.has_shipping_costs ?? 0,
    // Read by the V2 product card (KFT-4152) for its badge, chip and context label.
    is_online: eventGetters.getAttendanceMode(item) === 'ONLINE',
    accessibility: eventGetters.getIsAccessible(item),
    required_address: Boolean(eventGetters.getIsAddressRequired(item)),
    city: eventGetters.getAddressLocalityName(item) || undefined,
    team_event_only: eventGetters.getIsTeamEvent(item),
    is_group_pricing: item.is_group_pricing ?? false,
    group_pricings: item.group_pricings
      ? {
          default_price: item.group_pricings.default_price,
          original_price: item.group_pricings.original_price ?? undefined,
        }
      : undefined,
  }
}

export const transformToAlgoliaEvent = (data: { data: EventDescription[] }) =>
  (data?.data || []).map(parseEventDescriptionToAlgoliaEventItem)

const isRawEventDescription = (
  event: EventDescription | Partial<AlgoliaEventItem>
): event is EventDescription => {
  return (event as EventDescription)?.object === 'EventMetaData'
}

const enrichAndParseRawEvent = (
  event: EventDescription,
  supplierData: Supplier
): Partial<AlgoliaEventItem> => {
  const enriched = event.supplier?.data?.avg_review
    ? event
    : ({
        ...event,
        supplier: {
          data: { ...(event.supplier?.data || {}), ...supplierData },
        },
      } as EventDescription)

  return parseEventDescriptionToAlgoliaEventItem(enriched)
}

const enrichParsedEvent = (
  event: Partial<AlgoliaEventItem>,
  supplierData: Supplier
): Partial<AlgoliaEventItem> => {
  const ratingFields = extractEventRatingFields({
    avg_review: (event as any).avg_review ?? null,
    total_reviews: (event as any).totalRating ?? null,
    supplier: {
      data: {
        avg_review: supplierData.avg_review ?? null,
        google_avg_review: supplierData.google_avg_review ?? null,
        google_total_reviews: supplierData.google_total_reviews ?? null,
      },
    },
  })

  return { ...event, ...ratingFields }
}

/**
 * Enriches events with supplier rating data then normalizes to Algolia format.
 * Handles both raw EventDescription[] and already-parsed Partial<AlgoliaEventItem>[].
 */
export const enrichAndParseSupplierEvents = (
  events: (EventDescription | Partial<AlgoliaEventItem>)[],
  supplierData: Supplier
): Partial<AlgoliaEventItem>[] => {
  return events.map((event) =>
    isRawEventDescription(event)
      ? enrichAndParseRawEvent(event, supplierData)
      : enrichParsedEvent(event, supplierData)
  )
}
